<template>
    <div class="print-layout">
        <div v-for="day in days" :key="`day-${day.type}`" class="days">
            <div class="dates">
                {{
                    t('valid_from_to', {
                        startDate: getEffectiveDate(period.dateDebutPeriode),
                        endDate: getEffectiveDate(period.dateFinPeriode),
                    })
                }}
            </div>
            <table class="print-header">
                <tbody>
                    <tr>
                        <th>{{ t('route') }}</th>
                        <th>Direction</th>
                        <th>{{ t('schedule') }}</th>
                        <th class="accessible" rowspan="2">
                            <div v-if="route.accessible" class="img-accessible">
                                <img src="@@/img/accessible.svg" alt="" width="60" height="60" />
                                <span class="visuallyhidden">{{ $t('a11y.accessible.accessible') }}</span>
                            </div>
                            <div v-else class="visuallyhidden">
                                {{ $t('a11y.accessible.not_accessible') }}
                            </div>
                        </th>
                    </tr>
                    <tr>
                        <td>
                            <span class="no-route">{{ route.noParcours }}</span>
                        </td>
                        <td>
                            <span class="route-direction">
                                {{ direction.description }}
                            </span>
                        </td>
                        <td>
                            <div>
                                <div>{{ $t(`legend.${day.type}`) }}</div>
                                <div v-if="options.allDay">{{ t('time_all_day') }}</div>
                                <div v-else>
                                    {{
                                        t('time_from_to', {
                                            startTime: formatTime(options.from),
                                            endTime: formatTime(options.to),
                                        })
                                    }}
                                </div>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
            <div class="schedule">
                <table v-if="day.passages.length">
                    <thead>
                        <tr>
                            <th v-for="stop in stops" :key="`weekdays-${stop.noArret}`">
                                <span class="no-stop-wrapper">
                                    <span class="no-stop">
                                        {{ stop.noArret }}
                                    </span>
                                    <span v-if="route.accessible && stop.accessible" class="img-accessible">
                                        <img src="@@/img/accessible.svg" alt="" width="60" height="60" />
                                        <span class="visuallyhidden">{{ $t('a11y.accessible.accessible_stop') }}</span>
                                    </span>
                                </span>
                                <div>
                                    {{ stop.nom }}
                                </div>
                            </th>
                        </tr>
                    </thead>
                    <tbody :style="{ fontSize: `${options.size}pt` }">
                        <tr v-for="(departures, i) in day.passages" :key="`departure-${i}`">
                            <td v-for="(departure, j) in departuresPerRow(departures)" :key="`departure-${j}`">
                                <ScheduleItem
                                    :departure="departure"
                                    :route="route"
                                    :day-type="day.type"
                                ></ScheduleItem>
                            </td>
                        </tr>
                    </tbody>
                </table>
                <p v-else>{{ t('no_service_for_period') }}</p>
            </div>
            <div class="legend">
                <h2 class="print-header">{{ $t('legend.legend') }}</h2>
                <div class="legend-content">
                    <div>
                        <div class="legend-item">
                            <span>↑</span>&nbsp;{{ $t('legend.boarding_only') }}
                        </div>
                        <div class="legend-item">
                            <span>↓</span>&nbsp;{{ $t('legend.drop_off_only') }}
                        </div>
                        <div class="legend-item">
                            <span>LJ</span>&nbsp;{{ $t('legend.monday_to_thursdays') }}
                        </div>
                        <div class="legend-item">
                            <span>JV</span>&nbsp;{{ $t('legend.thursdays_friday_only') }}
                        </div>
                        <div class="legend-item"><span>V</span>&nbsp;{{ $t('legend.friday_only') }}</div>
                    </div>
                    <div>
                        <div v-if="day.type === 'weekdays'" v-html="t('holidays')"></div>
                        <br />
                        <div v-html="t('variation_warning')"></div>
                    </div>
                </div>
            </div>
            <div class="signature">
                <div class="signature-content">
                    <div class="signature-phone">
                        <img src="@@/img/print/signature_phone.png" alt="" width="20" />
                        {{ constants.PHONE_NUMBER.value }}
                    </div>
                    <div class="signature-site">
                        <img src="@@/img/print/signature_web.png" alt="" width="30" />
                        {{ constants.SITE_ALIAS }}
                    </div>
                    <div>
                        <a :href="constants.FACEBOOK_URL">
                            <img src="@@/img/print/signature_facebook.png" alt="" width="20" />
                        </a>
                    </div>
                    <div>
                        <a :href="constants.TWITTER_URL">
                            <img src="@@/img/print/signature_twitter.png" alt="" width="20" />
                        </a>
                    </div>
                </div>
                <img class="rtc-logo" src="@@/img/print/signature_rtc.png" alt="" width="70" />
            </div>
        </div>
        <div v-if="options.line" class="line">
            <img :src="route.urlTraceGIF" alt="" />
        </div>
    </div>
</template>

<script setup>
import { useI18n } from 'vue-i18n';

import { constants } from '@@/js/utils/constants';
import { useEffectiveDate } from '@/schedules/composables/useEffectiveDate';
import fr from '@/i18n/printable/fr.json';
import en from '@/i18n/printable/en.json';
import ScheduleItem from '@/shared/ScheduleItem';

const props = defineProps({
    period: {
        type: Object,
        default: null
    },
    route: {
        type: Object,
        default: null
    },
    direction: {
        type: Object,
        default: null
    },
    stops: {
        type: Array,
        default: () => []
    },
    days: {
        type: Array,
        default: () => []
    },
    options: {
        type: Object,
        default: () => ({})
    },
});

const { t, locale } = useI18n({
    messages: {
        fr,
        en,
    },
});

const { getEffectiveDate } = useEffectiveDate();

function formatTime(value) {
    if (locale.value === 'fr') {
        return value.replace(/:/, ' h ');
    } else {
        return value;
    }
}

function departuresPerRow(departures) {
    const row = [...departures];
    props.stops.forEach((stop, index) => {
        if (departures.map((p) => p.noStop).includes(stop.noArret) === false) {
            row.splice(index, 0, null);
        }
    });
    return row;
}
</script>

<style lang="scss" scoped>
.print-layout {
    padding: 0;
    text-align: left;
}
.days {
    margin-bottom: 40px;
}
.actions {
    padding: 10px 0;
    @media print {
        display: none;
    }
}
.dates {
    font-size: 16px;
    font-weight: 700;
    margin-bottom: 16px;
}
.print-header {
    width: 100%;
    padding: 16px;
    font-size: 16px;
    border: 1px solid $color-black;
    border-radius: 20px;
    @include breakpoint(medium) {
        padding: 16px 24px;
    }
    th {
        vertical-align: top;
        font-weight: 700;
        line-height: 1;
        text-align: left;
        min-width: 75px;
    }
    td {
        vertical-align: top;
        line-height: 1;
    }
    .accessible {
        vertical-align: middle;
        width: 10%;
        min-width: auto;
        img {
            width: 50px;
            height: auto;
        }
    }
    .no-route {
        font-size: 200%;
        @include breakpoint(medium) {
            font-size: 300%;
        }
    }
    .route-direction {
        @include breakpoint(medium) {
            font-size: 150%;
        }
    }
}
.schedule {
    margin: 24px 0;
    table {
        border-collapse: collapse;
        th {
            vertical-align: top;
            width: 166px;
            padding: 5px;
            font-weight: 400;
            border: 1px solid $color-black;
        }
        td {
            padding: 5px;
            text-align: left;
            border: 1px solid $color-black;
        }
    }
    .no-stop-wrapper {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 16px;
    }
    .no-stop {
        font-size: 120%;
        font-weight: 700;
    }
    .img-accessible {
        img {
            width: 20px;
            max-width: 20px;
            height: 20px;
        }
    }
}
.legend {
    margin: 10px 0;
    font-size: 16px;
}
.legend-content {
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 40px;
}
.legend-item {
    display: flex;
    gap: 8px;
    span {
        width: 20px;
        text-align: center
    }
}
.signature {
    display: flex;
    align-items: center;
    width: 100%;
    padding: 16px;
    font-size: 16px;
    border: 1px solid $color-black;
    border-radius: 20px;
    @include breakpoint(medium) {
        padding: 16px 24px;
    }
    .signature-site {
        font-weight: 700;
    }
    .signature-phone,
    .signature-site {
        display: flex;
        gap: 8px;
    }
}
.signature-content {
    display: flex;
    flex-grow: 1;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px 16px;
    img {
        flex-shrink: 0;
    }
}
.trace {
    img {
        width: 100%;
        max-width: 1000px;
    }
}
.spinner {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
}
a {
    &::after {
        @media print {
            content: none;
            display: none;
        }
    }
}
</style>
